import React, { useState, useEffect } from "react";
import Modal from "../base/Modal";
import { getAppResources } from "../../services/api.js";
import { DataState } from "../../context/DataProvider";
import { API_URL } from "../../services/api"
import Select, { components } from 'react-select';

const daysOfWeek = [
  { label: 'Mon', value: 'Monday' },
  { label: 'Tue', value: 'Tuesday' },
  { label: 'Wed', value: 'Wednesday' },
  { label: 'Thu', value: 'Thursday' },
  { label: 'Fri', value: 'Friday' },
  { label: 'Sat', value: 'Saturday' },
  { label: 'Sun', value: 'Sunday' },
];

const TriggerNodeModal = ({
  mode,
  nodeData,
  onSave,
  onDelete,
  onClose,
  appEventData,
  viewOnly,
  isBot = false // For Step Bot Special Criteria
}) => {
  // States for trigger type, option, events and parameters (existing logic)
  const [triggerType, setTriggerType] = useState(nodeData?.data?.triggerType || "");
  const [triggerOption, setTriggerOption] = useState(nodeData?.data?.triggerOption || "");
  const [events, setEvents] = useState(nodeData?.data?.events || []);
  const [specialBotTriggerCriteria, setSpecialBotTriggerCriteria] = useState(nodeData?.data?.specialCriteria || "")
  const [parameters, setParameters] = useState([]);
  const { userGoogleId } = DataState();

  // Extra states for Jira resource and project selection
  const [jiraResources, setJiraResources] = useState([]);
  const [selectedJiraResource, setSelectedJiraResource] = useState(null);
  const [selectedJiraProject, setSelectedJiraProject] = useState(null);
  const [jiraLoading, setJiraLoading] = useState(false);
  const [jiraError, setJiraError] = useState(null);

  // Github Repo
  const [githubRepos, setGithubRepos] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [githubLoading, setGithubLoading] = useState(false);
  const [githubError, setGithubError] = useState(null);
  const [dropdownOpenRepo, setDropdownOpenRepo] = useState(false);

  // ─── TEAMS/CHANNELS/CHATS ──────────────────────────
  const [teamsResources, setTeamsResources] = useState([]);
  const [chatsResources, setChatsResources] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState(null);

  // 1️⃣ Decide which resource pickers we need:
  const needsTeamPicker = events.some(e => e.startsWith('team_member'))
    || events.some(e => e.startsWith('channel_'));

  const needsChannelPicker = events.some(e => e.startsWith('channel_message'))
    || events.some(e => e.startsWith('channel_member'));

  const needsChatPicker = events.some(e => e.startsWith('chat_message'))
    || events.some(e => e.startsWith('chat_member'));

  useEffect(() => {
    console.log("Teams Resources:", teamsResources)
  }, [teamsResources])


  // New: Relative timing for jira:dueissue
  // Derive initial values from nodeData.data.relativeMinutes if present
  const rawRel = nodeData?.data?.time_offset ?? 0; // time_offset in ms
  const initDirection = rawRel <= 0 ? 'before' : 'after';
  const absMins = Math.floor(Math.abs(rawRel) / 60000);
  const initDays = Math.floor(absMins / (24 * 60));
  const initHours = Math.floor((absMins % (24 * 60)) / 60);
  const initMins = absMins % 60;

  const [relativeDirection, setRelativeDirection] = useState(initDirection);
  const [relativeDays, setRelativeDays] = useState(initDays);
  const [relativeHours, setRelativeHours] = useState(initHours);
  const [relativeMinutesField, setRelativeMinutesField] = useState(initMins);




  // Scheduler states with timezone conversion
  const utcTimeStored = nodeData?.data?.daytime ?? 0; // minutes since midnight UTC
  const tzOffset = new Date().getTimezoneOffset(); // minutes: local - UTC
  // Convert to local minutes since midnight, snapping to 0/30
  const rawLocal = ((utcTimeStored - tzOffset) % (24 * 60) + 24 * 60) % (24 * 60);
  const initHour = Math.floor(rawLocal / 60);
  const initMinute = rawLocal % 60 === 30 ? 30 : 0;
  const [schedHour, setSchedHour] = useState(initHour);
  const [schedMinute, setSchedMinute] = useState(initMinute);
  const [schedDays, setSchedDays] = useState(nodeData?.data?.days || []);

  // Compute human-readable offset string
  const totalOffset = -tzOffset; // minutes: UTC -> local
  const offsetSign = totalOffset >= 0 ? '+' : '-';
  const offsetHour = String(Math.floor(Math.abs(totalOffset) / 60)).padStart(2, '0');
  const offsetMin = String(Math.abs(totalOffset) % 60).padStart(2, '0');
  const offsetLabel = `UTC${offsetSign}${offsetHour}:${offsetMin}`;

  // Handlers
  const toggleDay = (day) => {
    setSchedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  // Filter options based on selected trigger type
  const typeOptions = appEventData.find((type) => type.type === triggerType)?.options || [];
  const selectedOption = typeOptions.find((opt) => opt.value === triggerOption);

  // Event options and parameters for the selected trigger option
  const eventOptions = selectedOption?.triggerEvents || [];
  const optionParameters = selectedOption?.parameters || [];

  // Initialize parameters by merging default and saved values
  useEffect(() => {
    const mergedParameters = optionParameters.map((param) => {
      const savedParam = nodeData?.data?.parameters?.find((p) => p.param === param.param);
      return {
        ...param,
        value: savedParam ? savedParam.value : param.value,
      };
    });
    setParameters(mergedParameters);
  }, []);

  // Fetch Jira resources when triggerOption is "jira"
  useEffect(() => {
    if (triggerOption === "jira" || triggerOption === "jira:dueissue") {
      const fetchJiraResources = async () => {
        try {
          setJiraLoading(true);
          const response = await getAppResources("jira", userGoogleId);
          // Normalize the Jira resources (using similar structure as ResourceModal)
          const normalizedResources = response.resources.map((resource) => ({
            id: resource.id,
            name: resource.name,
            avatarUrl: resource.avatarUrl || null,
            domainUrl: resource.url,
            projects: resource.projects || [],
          }));
          setJiraResources(normalizedResources);
          // Pre-select resource/project if available in nodeData
          if (nodeData && nodeData.data && nodeData.data.resource) {
            const foundResource = normalizedResources.find(
              (r) => r.id === nodeData.data.resource
            );
            if (foundResource) {
              setSelectedJiraResource(foundResource);
              if (nodeData.data.project) {
                const foundProject = foundResource.projects.find(
                  (p) => p.key === nodeData.data.project
                );
                setSelectedJiraProject(foundProject);
              }
            }
          }
        } catch (err) {
          setJiraError(err.message);
        } finally {
          setJiraLoading(false);
        }
      };
      fetchJiraResources();
    }
    else if (triggerOption === "github") {
      const fetchGithubRepos = async () => {
        try {
          setGithubLoading(true);
          const res = await fetch(`${API_URL}/github/repos-status`, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${userGoogleId}`,
            },
            credentials: "include"
          });
          const data = await res.json();
          console.log("Github repos fetch:", data)
          setGithubRepos(data.data || []);
          // Pre-select repo if available
          if (nodeData?.data?.repository) {
            const foundRepo = data.data.find(r => r.repo_id === nodeData.data.repository);
            if (foundRepo) setSelectedRepo(foundRepo);
          }
        } catch (error) {
          setGithubError(error.message);
        } finally {
          setGithubLoading(false);
        }
      };
      fetchGithubRepos();
    } // ─── fetch Teams + Chats ──────────────────────────
    else if (triggerOption === "microsoft_teams") {
      const fetchTeams = async () => {
        setTeamsLoading(true);
        try {
          const res = await fetch(`${API_URL}/microsoft_teams/resources`, {
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userGoogleId}` },
            credentials: 'include'
          });
          const json = await res.json();
          if (!json.success) throw new Error(json.message);

          // Build teams + channels
          const teams = json.data.teams.map(t => ({
            id: t.team_id,
            name: t.displayName,
            webhook_exists: t.webhook_exists,          // ← preserve this
            channels: json.data.channels
              .filter(c => c.team_id === t.team_id)
              .map(c => ({ id: c.channel_id, name: c.displayName, webhook_exists: c.webhook_exists }))
          }));

          // Build flat chats
          const chats = json.data.chats.map(ch => ({
            id: ch.chat_id,
            name: ch.topic || ch.chat_id,
            webhook_exists: ch.webhook_exists          // ← …and this
          }));

          setTeamsResources(teams);
          setChatsResources(chats);

          // Pre-select existing values (if editing)
          const {
            team_id: teamId,
            channel_id: channelId,
            chat_id: chatId
          } = nodeData?.data || {};
          if (teamId) {
            const t = teams.find(x => x.id === teamId);
            setSelectedTeam(t || null);
            if (channelId && t) {
              setSelectedChannel(t.channels.find(x => x.id === channelId) || null);
            }
          }
          if (chatId) {
            setSelectedChat(chats.find(x => x.id === chatId) || null);
          }
        } catch (err) {
          setTeamsError(err.message);
        } finally {
          setTeamsLoading(false);
        }
      };
      fetchTeams();
    }
  }, [triggerOption]);

  // Handle changes for events and parameters
  const handleEventChange = (event) => {
    const selectedEvent = event.target.value;
    setEvents((prevEvents) =>
      prevEvents.includes(selectedEvent)
        ? prevEvents.filter((e) => e !== selectedEvent)
        : [...prevEvents, selectedEvent]
    );
  };

  const handleParameterChange = (paramKey, value) => {
    setParameters((prev) =>
      prev.map((param) =>
        param.param === paramKey ? { ...param, value } : param
      )
    );
  };

  // Save the node with simplified parameters plus Jira resource/project if applicable
  const handleSave = () => {
    // Compute signed relative minutes only for jira:dueissue
    let signedRel = undefined;
    if (triggerOption === 'jira:dueissue') {
      const total = relativeDays * 24 * 60 + relativeHours * 60 + relativeMinutesField;
      const maxAllowed = 7 * 24 * 60;
      const clamped = Math.min(total, maxAllowed);
      signedRel = (relativeDirection === 'before' ? -clamped : clamped) * 60000; // converting to milliseconds
    }

    // Compute scheduler UTC minutes, ensure multiple of 30
    let schedulerPayload = {};
    if (triggerOption === 'schedular') {
      const localMinutes = schedHour * 60 + schedMinute;
      const utcRaw = ((localMinutes + tzOffset) % (24 * 60) + 24 * 60) % (24 * 60);
      // Snap to nearest 30
      const utcSnapped = Math.round(utcRaw / 30) * 30;
      schedulerPayload = {
        days: schedDays,
        daytime: utcSnapped,
      };
    }


    const updatedNode = {
      ...nodeData,
      data: {
        triggerType,
        triggerOption,
        label: selectedOption?.label || "",
        events,
        parameters: parameters.map(({ param, value }) => ({ param, value })), // Only save param and value
        ...(triggerOption === "jira" || triggerOption === "jira:dueissue" ? {
          resource: selectedJiraResource?.id || "",
          project: selectedJiraProject?.key || "",
        } : {}),
        ...(triggerOption === "jira:dueissue" ? { time_offset: signedRel } : {}),
        ...(triggerOption === "github" ? { repository: selectedRepo?.repo_id || "" } : {}),
        ...schedulerPayload,
        // … other fields …
        ...(triggerOption === "microsoft_teams" ? {
          team_id: selectedTeam?.id || "",
          channel_id: selectedChannel?.id || "",
          chat_id: selectedChat?.id || "",
        } : {}),

        // Add the special criteria to the data object if isBot is true
        ...(isBot && { specialCriteria: specialBotTriggerCriteria }),
      },
    };
    onSave(updatedNode);
  };

  return (
    <Modal
      title={mode === "add" ? "Add Trigger" : "Edit Trigger"}
      isOpen={true}
      onClose={onClose}
    >
      <div className="p-4 h-[80vh] overflow-y-auto bg-zinc-900 rounded-lg shadow-md">
        <div className="p-4">
          {/* Trigger Type Selection */}
          <label className="block text-sm font-medium text-zinc-100 mb-2">
            {viewOnly ? "Trigger Type" : "Select Trigger Type"}
          </label>
          <select
            className="w-full border border-zinc-700 bg-zinc-800 text-zinc-100 p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#5652CF] mb-4"
            value={triggerType}
            disabled={viewOnly}
            onChange={(e) => {
              setTriggerType(e.target.value);
              setTriggerOption(""); // Reset the option when type changes
              setEvents([]);
              setParameters([]);
            }}
          >
            <option value="">Select Trigger Type</option>
            {appEventData.map((type) => (
              <option key={type.type} value={type.type}>
                {type.type}
              </option>
            ))}
          </select>

          {/* Trigger Option Selection */}
          {triggerType && (
            <>
              <label className="block text-sm font-medium text-zinc-100 mb-2">
                Select Trigger Option
              </label>
              <select
                disabled={viewOnly}
                className="w-full border border-zinc-700 bg-zinc-800 text-zinc-100 p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#5652CF] mb-4"
                value={triggerOption}
                onChange={(e) => {
                  setTriggerOption(e.target.value);
                  setEvents([]);
                  setParameters(
                    typeOptions.find((opt) => opt.value === e.target.value)?.parameters || []
                  );
                }}
              >
                <option value="">Select Trigger Option</option>
                {typeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </>
          )}

          {/* Extra Jira Fields */}
          {(triggerOption === "jira" || triggerOption === "jira:dueissue") && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-zinc-100 mb-2">
                Select Jira Resource
              </label>
              {jiraLoading ? (
                <p className="text-zinc-300">Loading Jira resources...</p>
              ) : jiraError ? (
                <p className="text-red-500">{jiraError}</p>
              ) : (
                <select
                  disabled={viewOnly}
                  className="w-full border border-zinc-700 bg-zinc-800 text-zinc-100 p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#5652CF] mb-4"
                  value={selectedJiraResource ? selectedJiraResource.id : ""}
                  onChange={(e) => {
                    const resource = jiraResources.find((r) => r.id === e.target.value);
                    setSelectedJiraResource(resource);
                    setSelectedJiraProject(null); // Reset project selection when resource changes
                  }}
                >
                  <option value="">Select a Jira Resource</option>
                  {jiraResources.map((resource) => (
                    <option key={resource.id} value={resource.id}>
                      {resource.name}
                    </option>
                  ))}
                </select>
              )}

              {selectedJiraResource && (
                <>
                  <label className="block text-sm font-medium text-zinc-100 mb-2">
                    Select Jira Project
                  </label>
                  <select
                    disabled={viewOnly}
                    className="w-full border border-zinc-700 bg-zinc-800 text-zinc-100 p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#5652CF] mb-4"
                    value={selectedJiraProject ? selectedJiraProject.key : ""}
                    onChange={(e) => {
                      const project = selectedJiraResource.projects.find(
                        (p) => p.key === e.target.value
                      );
                      setSelectedJiraProject(project);
                    }}
                  >
                    <option value="">Select a Jira Project</option>
                    {selectedJiraResource.projects.map((project) => (
                      <option key={project.key} value={project.key}>
                        {project.name} ({project.key})
                      </option>
                    ))}
                  </select>
                </>
              )}
              {/* New: Relative timing only for jira:dueissue */}
              {triggerOption === 'jira:dueissue' && (
                <div className="mt-4 p-4 bg-zinc-800 rounded">
                  <label className="block text-sm font-medium text-zinc-100 mb-2">
                    Trigger Time Relative to Due Date
                  </label>
                  <div className="flex space-x-3 mb-3">
                    {['before', 'after'].map(opt => (
                      <button
                        key={opt}
                        type="button"
                        disabled={viewOnly}
                        onClick={() => setRelativeDirection(opt)}
                        className={`px-4 py-1 rounded-full text-sm border transition ${relativeDirection === opt
                          ? 'bg-[#5652CF] text-white border-[#5652CF]'
                          : 'bg-zinc-700 text-zinc-300 border-zinc-600 hover:bg-zinc-600'}`}
                      >
                        {opt.charAt(0).toUpperCase() + opt.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs mb-1 text-zinc-300">Days</label>
                      <input
                        type="number"
                        disabled={viewOnly}
                        min={0} max={7}
                        value={relativeDays}
                        onChange={e => setRelativeDays(Math.min(7, Math.max(0, parseInt(e.target.value) || 0)))}
                        className="w-full px-2 py-1 bg-zinc-700 text-zinc-100 rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-300 mb-1">Hours</label>
                      <input
                        type="number"
                        disabled={viewOnly}
                        min={0} max={23}
                        value={relativeHours}
                        onChange={e => setRelativeHours(Math.min(23, Math.max(0, parseInt(e.target.value) || 0)))}
                        className="w-full px-2 py-1 bg-zinc-700 text-zinc-100 rounded"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1 block">Minutes</label>
                      <div className="flex space-x-2">
                        {[0, 30].map((val) => (
                          <button
                            key={val}
                            onClick={() => setRelativeMinutesField(val)}
                            className={`px-4 py-1 rounded-lg text-sm text-zinc-300 border transition-all duration-200 ${relativeMinutesField === val
                              ? 'bg-blue-600 border-blue-600'
                              : 'bg-gray-700 border-gray-600 hover:border-blue-400 hover:text-zin-300'
                              }`}
                          >
                            {val.toString().padStart(2, '0')}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  {(relativeDays * 24 * 60 + relativeHours * 60 + relativeMinutesField) > 7 * 24 * 60 && (
                    <p className="text-red-500 text-xs mt-1">Maximum of 7 days is allowed.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Scheduler UI */}
          {triggerOption === 'schedular' && (
            <div className="mb-4 p-4 bg-zinc-800 rounded-lg">
              <label className="block text-sm font-medium text-zinc-100 mb-2">
                Schedule Time (Local, {offsetLabel})
              </label>
              <div className="flex items-center space-x-3 mb-4">
                <input
                  type="number"
                  min={0} max={23}
                  disabled={viewOnly}
                  value={schedHour}
                  onChange={e => setSchedHour(Math.min(23, Math.max(0, +e.target.value || 0)))}
                  className="w-16 px-3 py-2 bg-zinc-700 text-zinc-100 rounded"
                />
                <span className="text-zinc-300">:</span>
                <div className="flex space-x-2">
                  {[0, 30].map(val => (
                    <button
                      key={val}
                      type="button"
                      disabled={viewOnly}
                      onClick={() => setSchedMinute(val)}
                      className={`px-4 py-1 rounded-full text-sm border transition ${schedMinute === val
                        ? 'bg-[#5652CF] text-white border-[#5652CF]'
                        : 'bg-zinc-700 text-zinc-300 border-zinc-600 hover:bg-zinc-600'}`}
                    >
                      {val.toString().padStart(2, '0')}
                    </button>
                  ))}
                </div>
              </div>

              <label className="block text-sm font-medium text-zinc-100 mb-2">
                Select Days
              </label>
              <div className="flex flex-wrap gap-2">
                {daysOfWeek.map(d => (
                  <button
                    key={d.value}
                    type="button"
                    disabled={viewOnly}
                    onClick={() => toggleDay(d.value)}
                    className={`px-3 py-1 rounded-full text-sm transition ${schedDays.includes(d.value)
                      ? 'bg-[#5652CF] text-white border-[#5652CF]'
                      : 'bg-zinc-700 text-zinc-300 border-zinc-600 hover:bg-zinc-600'}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {triggerOption === "github" && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-zinc-100 mb-2">
                Select GitHub Repository
              </label>
              {githubLoading ? (
                <p className="text-zinc-300">Loading repositories...</p>
              ) : githubError ? (
                <p className="text-red-500">{githubError}</p>
              ) : (
                // Custom dropdown replacement for the native <select>
                <div className="relative">
                  <button
                    type="button"
                    disabled={viewOnly}
                    onClick={() => setDropdownOpenRepo(!dropdownOpenRepo)}
                    className="w-full border border-zinc-700 bg-zinc-800 text-zinc-100 p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#5652CF] text-left"
                  >
                    {selectedRepo ? (
                      <div className="flex justify-between items-center">
                        <div className="flex">
                          {(() => {
                            const [owner, repoName] = selectedRepo.repo_full_name.split('/');
                            return (
                              <>
                                <span className="font-bold mr-1">{owner}</span>
                                <span className="mr-1">/</span>
                                <span>{repoName}</span>
                              </>
                            );
                          })()}
                        </div>
                        <div>
                          {selectedRepo.webhook_exists ? (
                            <span className="text-green-500">✅</span>
                          ) : (
                            <span className="text-red-500">❌</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400">Select a Repository</span>
                    )}
                  </button>

                  {dropdownOpenRepo && (
                    <div className="absolute z-10 w-full mt-1 bg-zinc-800 border border-zinc-700 rounded shadow-lg">
                      {githubRepos.map((repo) => {
                        const [owner, repoName] = repo.repo_full_name.split('/');
                        return (
                          <div
                            key={repo.repo_id}
                            onClick={() => {
                              setSelectedRepo(repo);
                              setDropdownOpenRepo(false);
                            }}
                            className="cursor-pointer hover:bg-[#5652CF] hover:text-white p-2 flex justify-between items-center"
                          >
                            <div className="flex">
                              <span className="font-bold mr-1">{owner}</span>
                              <span className="mr-1">/</span>
                              <span>{repoName}</span>
                            </div>
                            <div>
                              {repo.webhook_exists ? (
                                <span className="text-green-500">✅</span>
                              ) : (
                                <span className="text-red-500">❌</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Trigger Events */}
          {triggerOption && eventOptions.length > 0 && (
            <>
              <label className="block text-sm font-medium text-zinc-100 mb-2">
                {viewOnly ? "Trigger Events" : "Select Trigger Events"}
              </label>
              <div className="flex flex-wrap gap-2 mb-4">
                {eventOptions.map((event) => (
                  <label
                    key={event}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 ${events.includes(event)
                      ? "bg-zinc-100 text-zinc-800 hover:bg-zinc-300"
                      : "bg-zinc-800 text-zinc-100 hover:bg-zinc-700 hover:text-zinc-100"
                      }`}
                  >
                    <input
                      type="checkbox"
                      disabled={viewOnly}
                      checked={events.includes(event)}
                      onChange={handleEventChange}
                      value={event}
                      className="hidden"
                    />
                    {event}
                  </label>
                ))}
              </div>
            </>
          )}

         
          {triggerOption === "microsoft_teams"  && (
            <div className="mb-4 p-4 bg-zinc-800 rounded-lg space-y-4">
              {/* <div className="mb-4"> */}
              <label className="block text-sm font-medium text-zinc-100 mb-2">Microsoft Teams Resources</label>
              {teamsLoading && (
                <p className="text-zinc-300">Loading Teams resources...</p>
              )}
              {teamsError && (
                <p className="text-red-500">Error: {teamsError}</p>
              )}
              {
                !teamsLoading && !teamsError && (
                  <>
                  {/* TEAM picker */}
              {needsTeamPicker && (
                <>
                  <label className="block text-sm text-zinc-100">Select Team</label>
                  <select
                    className="w-full p-2 bg-zinc-700 text-zinc-100 rounded"
                    value={selectedTeam?.id || ""}
                    onChange={e => {
                      const t = teamsResources.find(x => x.id === e.target.value);
                      setSelectedTeam(t);
                      setSelectedChannel(null);
                    }}
                  >
                    <option value="">— Choose a Team —</option>
                    {teamsResources.map(t => (
                      <option key={t.id} value={t.id}>{t.name} {t.webhook_exists ? '✅' : '❌'}</option>
                    ))}
                  </select>
                </>
              )}

              {/* CHANNEL picker (only after team) */}
              {needsChannelPicker && selectedTeam && (
                <>
                  <label className="block text-sm text-zinc-100">Select Channel</label>
                  <select
                    className="w-full p-2 bg-zinc-700 text-zinc-100 rounded"
                    value={selectedChannel?.id || ""}
                    onChange={e => {
                      const c = selectedTeam.channels.find(x => x.id === e.target.value);
                      setSelectedChannel(c);
                    }}
                  >
                    <option value="">— Choose a Channel —</option>
                    {selectedTeam.channels.map(c => (
                      <option key={c.id} value={c.id}>{c.name} {c.webhook_exists ? '✅' : '❌'}</option>
                    ))}
                  </select>
                </>
              )}

              {/* CHAT picker */}
              {needsChatPicker && (
                <>
                  <label className="block text-sm text-zinc-100">Select Chat</label>
                  <select
                    className="w-full p-2 bg-zinc-700 text-zinc-100 rounded"
                    value={selectedChat?.id || ""}
                    onChange={e => {
                      const ch = chatsResources.find(x => x.id === e.target.value);
                      setSelectedChat(ch);
                    }}
                  >
                    <option value="">— Choose a Chat —</option>
                    {chatsResources.map(ch => (
                      <option key={ch.id} value={ch.id}> {ch.name} {ch.webhook_exists ? '✅' : '❌'}</option>
                    ))}
                  </select>
                </>
              )}
                  </>
                )
              }

              

            </div>
          )}
          {/* --- NEW SECTION FOR SPECIAL TRIGGER CRITERIA --- */}
          {isBot && triggerType == "webhook" && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-zinc-100 mb-2">
                Special Trigger Criteria
              </label>
              <textarea
                value={specialBotTriggerCriteria}
                onChange={(e) => setSpecialBotTriggerCriteria(e.target.value)}
                disabled={viewOnly}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-zinc-100 rounded-md placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#5652CF] focus:border-transparent transition duration-200 resize-y"
                rows="3"
                placeholder="e.g., Mail is related to UI Bugs on the Oqlous Website"
              />
              <p className="text-xs text-zinc-400 mt-1">
                Provide specific, natural language instructions to filter when
                this trigger should run.
              </p>
            </div>
          )}

          {/* Parameters Section */}
          {parameters.map((param) => (
            <div key={param.param} className="mb-4">
              <label className="block text-sm font-medium text-zinc-100 mb-1">
                {param.label}
                {param.required && <span className="text-red-500"> *</span>}
              </label>
              {param.type === "array" && param.enum ? (
                <ListInputWithEnum
                  value={param.value || []}
                  viewOnly={viewOnly}
                  enumValues={param.enum}
                  onChange={(newList) => handleParameterChange(param.param, newList)}
                />
              ) : param.enum ? (
                <select
                  disabled={viewOnly}
                  className="w-full border border-zinc-700 bg-zinc-800 text-zinc-100 p-3 rounded focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
                  value={param.value || ""}
                  onChange={(e) => handleParameterChange(param.param, e.target.value)}
                >
                  <option value="">Select an option</option>
                  {param.enum.map((option, i) => (
                    <option key={i} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : param.type === "array" ? (
                <ListInput
                  viewOnly={viewOnly}
                  value={param.value || []}
                  onChange={(newList) => handleParameterChange(param.param, newList)}
                />
              ) : param.type === "textarea" ? (
                <textarea
                  disabled={viewOnly}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-zinc-100 rounded-md placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#5652CF] focus:border-transparent transition duration-200 resize-none"
                  value={param.value}
                  onChange={(e) => handleParameterChange(param.param, e.target.value)}
                />
              ) : (
                <input
                  type="text"
                  disabled={viewOnly || param?.type === "static"}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-zinc-100 rounded-md placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#5652CF] focus:border-transparent transition duration-200"
                  value={param.value}
                  onChange={(e) => handleParameterChange(param.param, e.target.value)}
                />
              )}
            </div>
          ))}

          {/* Action Buttons */}
          {!viewOnly && (
            <div className="flex justify-end gap-3 mt-2">
              {mode === "edit" && (
                <button
                  className="px-4 py-2 text-zinc-300 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md transition duration-200 focus:outline-none focus:ring-2 focus:ring-zinc-500"
                  onClick={() => onDelete(nodeData.id)}
                >
                  Delete Trigger
                </button>
              )}
              <button
                className="px-4 py-2 text-zinc-300 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md transition duration-200 focus:outline-none focus:ring-2 focus:ring-zinc-500"
                onClick={handleSave}
              >
                {mode === "add" ? "Add Trigger" : "Save Changes"}
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

const ListInput = ({ value, onChange, viewOnly }) => {
  const [list, setList] = useState(value);

  const addItem = () => setList([...list, ""]);
  const updateItem = (index, newValue) => {
    const updatedList = [...list];
    updatedList[index] = newValue;
    setList(updatedList);
    onChange(updatedList);
  };
  const removeItem = (index) => {
    const updatedList = list.filter((_, i) => i !== index);
    setList(updatedList);
    onChange(updatedList);
  };

  return (
    <div>
      {list.map((item, index) => (
        <div key={index} className="flex items-center mb-2">
          <input
            type="text"
            disabled={viewOnly}
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-zinc-100 rounded-md placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#5652CF] focus:border-transparent transition duration-200"
            value={item}
            onChange={(e) => updateItem(index, e.target.value)}
          />
          {!viewOnly && (
            <button
              className="ml-2 px-2 py-1 bg-zinc-800 border border-zinc-700 text-white rounded"
              onClick={() => removeItem(index)}
            >
              ✕
            </button>
          )}
        </div>
      ))}
      {!viewOnly && (
        <button
          className="px-2 py-1 text-sm my-1 text-zinc-300 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md transition duration-200 focus:outline-none focus:ring-2 focus:ring-zinc-500"
          onClick={addItem}
        >
          Add Item
        </button>
      )}
    </div>
  );
};

const ListInputWithEnum = ({ value, enumValues, onChange, viewOnly }) => {
  const [list, setList] = useState(value);

  const addItem = () => setList([...list, enumValues[0] || ""]);
  const updateItem = (index, newValue) => {
    const updatedList = [...list];
    updatedList[index] = newValue;
    setList(updatedList);
    onChange(updatedList);
  };
  const removeItem = (index) => {
    const updatedList = list.filter((_, i) => i !== index);
    setList(updatedList);
    onChange(updatedList);
  };

  return (
    <div>
      {list.map((item, index) => (
        <div key={index} className="flex items-center mb-2">
          <select
            disabled={viewOnly}
            className="w-full border border-zinc-700 bg-zinc-800 text-zinc-100 p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#5652CF] mb-4"
            value={item}
            onChange={(e) => updateItem(index, e.target.value)}
          >
            {enumValues.map((option, i) => (
              <option key={i} value={option}>
                {option}
              </option>
            ))}
          </select>
          {!viewOnly && (<button
            className="ml-2 px-2 py-1 bg-zinc-800 border border-zinc-700 text-white rounded"
            onClick={() => removeItem(index)}
          >
            ✕
          </button>)}

        </div>
      ))}
      {!viewOnly && (<button
        className="mt-2 px-4 py-2 bg-[#5652CF] text-white rounded hover:bg-blue-600"
        onClick={addItem}
      >
        Add Item
      </button>)}

    </div>
  );
};


export default TriggerNodeModal;
